import { RichTextView } from '@/components/vyroba/RichText';
import type { VykazData, VykazRow } from '@/components/vyroba/types';

/** Sekundy → „1 hod, 43 min 46 sec". */
function fmtDur(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0 ? `${h} hod, ${m} min ${s} sec` : `${m} min ${s} sec`;
}

/** UTC z Tabidoo → „04.05.2026 06:04:34" v našem čase. */
function fmtDateTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Barva pozadí buňky s výkonem — stejné hranice jako v přehledu. */
function perfBg(pct: number): string {
  if (pct >= 75) return '#d1fae5';
  if (pct >= 50) return '#fef3c7';
  return '#fee2e2';
}

/** Kusy po kategoriích, v pořadí, jak se objevily v zakázkách. */
function categories(rows: VykazRow[]): { category: string; qty: number }[] {
  const map = new Map<string, number>();
  for (const r of rows) map.set(r.category || '—', (map.get(r.category || '—') ?? 0) + r.total_qty);
  return [...map].map(([category, qty]) => ({ category, qty }));
}

/** Kusy per velikost uvnitř jedné kategorie (pro sloupcový graf). */
function sizesByCategory(rows: VykazRow[]): Map<string, { label: string; qty: number }[]> {
  const out = new Map<string, Map<string, number>>();
  for (const r of rows) {
    const cat = r.category || '—';
    const inner = out.get(cat) ?? new Map<string, number>();
    for (const s of r.sizes) inner.set(s.label, (inner.get(s.label) ?? 0) + s.qty);
    out.set(cat, inner);
  }
  // Velikosti seřadíme číselně (86/92 < 98/104 < …), textové názvy abecedně.
  return new Map(
    [...out].map(([cat, inner]) => [
      cat,
      [...inner]
        .map(([label, qty]) => ({ label, qty }))
        .sort((a, b) => {
          const na = Number.parseInt(a.label, 10);
          const nb = Number.parseInt(b.label, 10);
          if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
          return a.label.localeCompare(b.label, 'cs');
        }),
    ]),
  );
}

/**
 * Sestava výkazu v A4 stránkách: strana 1 souhrn + zakázky, strana 2 kategorie,
 * dál sloupcové grafy velikostí. Zobrazuje se na vlastní stránce (nový panel)
 * a odtud se tiskne / ukládá do PDF.
 */
export function VykazReport({
  data,
  note,
  errors,
  svadlenka,
  month,
  year,
}: {
  data: VykazData;
  note: string;
  errors: number;
  svadlenka: string;
  month: number;
  year: number;
}) {
  const cats = categories(data.rows);
  const sizes = sizesByCategory(data.rows);
  const errorRate = data.summary.total_qty > 0 ? (errors / data.summary.total_qty) * 100 : 0;

  return (
    <div id="vykaz-report">
      {/* ── Strana 1: souhrn + zakázky ─────────────────────────────────── */}
      <section className="page">
        <h1 style={{ textAlign: 'center', fontSize: 26, margin: '0 0 6px' }}>VÝKAZ PRÁCE</h1>
        <h2 style={{ textAlign: 'center', fontSize: 18, margin: '0 0 22px' }}>
          {String(month).padStart(2, '0')}/{year}
        </h2>

        <div style={{ display: 'flex', gap: 32, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>Informace</h3>
            <div style={{ fontSize: 13, lineHeight: 1.65 }}>
              <div>
                <strong>Jméno:</strong> {svadlenka}
              </div>
              <div>
                <strong>Počet zakázek:</strong> {data.summary.orders}
              </div>
              <div>
                <strong>Předpokládaný čas šití celkem:</strong> {data.summary.norm_hours.toFixed(1)}
                h
              </div>
              <div>
                <strong>Čas šití celkem:</strong> {data.summary.sewing_hours.toFixed(1)}h
              </div>
              <div>
                <strong>Celkový počet kusů:</strong> {data.summary.total_qty}
              </div>
              <div>
                <strong>Počet chyb:</strong> {errors}
              </div>
              <div>
                <strong>Chybovost:</strong> {errorRate.toFixed(1)}%
              </div>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>Poznámky</h3>
            <RichTextView html={note} className="vykaz-note" />
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>ID zakázky</th>
              <th>Kód produktu</th>
              <th>Celkem ks</th>
              <th>Švadlenka</th>
              <th>Start šití</th>
              <th>Konec šití</th>
              <th>Předpokládaný čas šití</th>
              <th>Doba šití</th>
              <th>Výkon</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id_zakazky}>
                <td style={{ textAlign: 'center' }}>{r.id_zakazky}</td>
                <td style={{ textAlign: 'center' }}>{r.product_code}</td>
                <td style={{ textAlign: 'center' }}>{r.total_qty}</td>
                <td style={{ textAlign: 'center' }}>{r.svadlenka}</td>
                <td style={{ textAlign: 'center' }}>{fmtDateTime(r.start)}</td>
                <td style={{ textAlign: 'center' }}>{fmtDateTime(r.konec)}</td>
                <td style={{ textAlign: 'center' }}>{r.norm_text}</td>
                <td style={{ textAlign: 'center' }}>{fmtDur(r.duration_sec)}</td>
                <td style={{ textAlign: 'center', background: perfBg(r.performance) }}>
                  {r.performance.toFixed(2)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ── Strana 2: kusy po kategoriích ──────────────────────────────── */}
      <section className="page">
        <h1 style={{ textAlign: 'center', fontSize: 20, margin: '0 0 20px' }}>
          VÝKAZ DLE KATEGORIÍ
        </h1>
        <table style={{ maxWidth: 480, margin: '0 auto' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Kategorie</th>
              <th>Celkem</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.category}>
                <td>{c.category}</td>
                <td style={{ textAlign: 'center' }}>{c.qty}</td>
              </tr>
            ))}
            <tr>
              <td>
                <strong>Celkem</strong>
              </td>
              <td style={{ textAlign: 'center' }}>
                <strong>{data.summary.total_qty}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* ── Další strany: grafy velikostí (2 grafy na stránku) ─────────── */}
      {chunk(
        [...sizes].filter(([, s]) => s.length > 0),
        2,
      ).map((pair, i) => (
        <section className="page" key={pair.map(([c]) => c).join('|')}>
          {i === 0 && (
            <h1 style={{ textAlign: 'center', fontSize: 20, margin: '0 0 20px' }}>
              PŘEHLED VELIKOSTÍ PODLE KATEGORIÍ
            </h1>
          )}
          {pair.map(([category, list]) => (
            <BarChart key={category} title={category} data={list} />
          ))}
        </section>
      ))}
    </div>
  );
}

/** Rozdělí pole na skupiny po `size` — dva grafy na stránku. */
function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** Jednoduchý sloupcový graf v SVG — bez knihovny, aby PDF nic nestahovalo. */
function BarChart({ title, data }: { title: string; data: { label: string; qty: number }[] }) {
  const W = 640;
  const H = 240;
  const padL = 40;
  const padB = 34;
  const padT = 24;
  const max = Math.max(1, ...data.map((d) => d.qty));
  const step = Math.max(1, Math.ceil(max / 5));
  const ticks = Array.from({ length: Math.floor(max / step) + 1 }, (_, i) => i * step);
  const bw = (W - padL - 10) / data.length;

  return (
    <div style={{ marginBottom: 24, breakInside: 'avoid' }}>
      <div style={{ textAlign: 'center', fontSize: 11, marginBottom: 4 }}>{title}</div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto' }}
        role="img"
        aria-label={`Počet kusů podle velikostí — ${title}`}
      >
        <title>{title}</title>
        {ticks.map((t) => {
          const y = H - padB - ((H - padB - padT) * t) / (ticks[ticks.length - 1] || 1);
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - 10} y2={y} stroke="#e5e7eb" strokeWidth="1" />
              <text x={padL - 6} y={y + 3} fontSize="9" textAnchor="end" fill="#6b7280">
                {t}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const full = ticks[ticks.length - 1] || 1;
          const h = ((H - padB - padT) * d.qty) / full;
          const x = padL + i * bw + bw * 0.2;
          const y = H - padB - h;
          return (
            <g key={d.label}>
              {d.qty > 0 && <rect x={x} y={y} width={bw * 0.6} height={h} fill="#7ec8f0" />}
              {d.qty > 0 && (
                <text
                  x={x + bw * 0.3}
                  y={y - 4}
                  fontSize="9"
                  fontWeight="bold"
                  textAnchor="middle"
                  fill="#374151"
                >
                  {d.qty}
                </text>
              )}
              <text
                x={x + bw * 0.3}
                y={H - padB + 14}
                fontSize="9"
                textAnchor="middle"
                fill="#6b7280"
              >
                {d.label}
              </text>
            </g>
          );
        })}
        <line x1={padL} y1={H - padB} x2={W - 10} y2={H - padB} stroke="#9ca3af" strokeWidth="1" />
        <text x={W / 2} y={H - 4} fontSize="9" textAnchor="middle" fill="#6b7280">
          Velikosti
        </text>
      </svg>
    </div>
  );
}
